// ./src/hooks/useEvents.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Event,
  EventService,
  EventFilter,
  EventInput,
  EventImportInput,
  EventImportStats,
  Pagination,
  SortItem,
} from "@/utils";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseEventsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<EventFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseEventsReturn {
  // Data state
  event: Event | null;
  events: Event[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<EventFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isImporting: boolean;
  removingEventId: string | null;
  selectedEvent: Event | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<EventFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Event[]>;
  create: (params: EventInput) => Promise<Event | null>;
  update: (id: string, params: EventInput) => Promise<Event | null>;
  remove: (id: string) => Promise<boolean>;

  // Special operations (unique to events)
  import: (params: EventImportInput) => Promise<EventImportStats | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<EventFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedEvent: (event: Event | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  eventsByDate: Record<string, Event[]>;
  eventsByStatus: Record<string, Event[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [
  { key: "events.eventDate", direction: "asc" },
];

export const useEvents = (options: UseEventsOptions = {}): UseEventsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [event, setEvent] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<EventFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [removingEventId, setRemovingEventId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => events.length === 0 && !loading,
    [events.length, loading]
  );

  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const date = event.eventDate ? event.eventDate.split("T")[0] : "unknown";
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(event);
      return acc;
    }, {} as Record<string, Event[]>);
  }, [events]);

  const eventsByStatus = useMemo(() => {
    return events.reduce((acc, event) => {
      const status = event.status || "unknown";
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(event);
      return acc;
    }, {} as Record<string, Event[]>);
  }, [events]);

  // Helper function to convert SortConfig to SortItem
  const convertSortConfigToSortItems = useCallback(
    (sortConfig: SortConfig[]): SortItem[] => {
      return sortConfig.map((sort) => ({
        name: sort.key,
        order: sort.direction.toUpperCase() as "ASC" | "DESC",
      }));
    },
    []
  );

  // Helper function to update event in list if it exists, or add it if not
  const updateOrAddEventInList = useCallback((updatedEvent: Event) => {
    setEvents((prev) => {
      const existingIndex = prev.findIndex((e) => e.id === updatedEvent.id);
      if (existingIndex >= 0) {
        const newEvents = [...prev];
        newEvents[existingIndex] = updatedEvent;
        return newEvents;
      } else {
        return [updatedEvent, ...prev];
      }
    });
  }, []);

  // Helper function to remove event from list if it exists
  const removeEventFromList = useCallback((eventId: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setEvent(null);
    setEvents([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsImporting(false);
    setRemovingEventId(null);
    setSelectedEvent(null);
  }, []);

  // Get single event (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await EventService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const eventData = result.data[0] as Event;
          setEvent(eventData);
          updateOrAddEventInList(eventData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load event";
          setLastError(errorMessage);
          setEvent(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load event";
        setLastError(errorMessage);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddEventInList]
  );

  // List events (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<EventFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setEvents([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = convertSortConfigToSortItems(currentSortBy);

        const result = await EventService.list(
          filter as EventFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEvents = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setEvents((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              const uniqueNewEvents = newEvents.filter(
                (e) => !existingIds.has(e.id)
              );
              return [...prev, ...uniqueNewEvents];
            });
          } else {
            setEvents(newEvents);
          }

          setHasMore(newEvents.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load events";
          setLastError(errorMessage);
          if (!isAppending) setEvents([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load events";
        setLastError(errorMessage);
        if (!isAppending) setEvents([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [convertSortConfigToSortItems]
  );

  // Get many events by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Event[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await EventService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedEvents = Array.isArray(result.data) ? result.data : [];
          loadedEvents.forEach((event) => {
            updateOrAddEventInList(event);
          });
          setLastError(null);
          return loadedEvents;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load events";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load events";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddEventInList]
  );

  // Create event (matching API method name)
  const create = useCallback(
    async (params: EventInput): Promise<Event | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await EventService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEvent = result.data[0] as Event;
          setEvent(newEvent);
          setEvents((prev) => [newEvent, ...prev]);
          setLastError(null);
          return newEvent;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create event";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create event";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update event (matching API method name)
  const update = useCallback(
    async (id: string, params: EventInput): Promise<Event | null> => {
      if (!id) {
        setLastError("Event ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EventService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEvent = result.data[0] as Event;
          setEvent(updatedEvent);
          updateOrAddEventInList(updatedEvent);
          setLastError(null);
          return updatedEvent;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update event";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update event";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddEventInList]
  );

  // Remove event (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Event ID is required for removal");
        return false;
      }

      setRemovingEventId(id);
      setLastError(null);

      try {
        const result = await EventService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setEvent(null);
          removeEventFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove event";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove event";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingEventId(null);
      }
    },
    [removeEventFromList]
  );

  // Import events (special operation)
  const importEvents = useCallback(
    async (params: EventImportInput): Promise<EventImportStats | null> => {
      setIsImporting(true);
      setLastError(null);

      try {
        const result = await EventService.import(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          // After successful import, refresh the events list
          await list(
            filters,
            { page: 1, pageSize: pageSizeRef.current },
            sortByRef.current,
            true
          );
          setLastError(null);
          return result.data[0];
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to import events";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to import events";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsImporting(false);
      }
    },
    [list, filters]
  );

  // Load more events
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh events
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    event,
    events,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    isImporting,
    removingEventId,
    selectedEvent,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    import: importEvents,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedEvent,
    reset,
    eventsByDate,
    eventsByStatus,
    lastError,
    clearError,
  };
};
